import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { CheckCircle2, Clock, Copy } from 'lucide-react';
import { subscriptionAPI } from '../api';
import { Alert, AlertDescription, AlertTitle } from './ui/alert';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Separator } from './ui/separator';
import {
  PENDING, PERIOD_WORDING, TAXES_WORDING, isReceived, longDate, money, plainAmount, priceWording, shortMoney,
  stateLabel,
} from '../lib/subscription';
import { copyText } from '../lib/clipboard';

// « Mon compte » → « Abonnement » (docs/specs/subscription-page): the state first, then what to do, then the
// details. Every term comes from GET /api/subscription; an unset one reads PENDING, never a default.

const Pending = ({ term }) => (
  <span className="text-muted-foreground italic" data-testid={`term-pending-${term}`}>{PENDING}</span>
);

const CopyButton = ({ value, what, copied, testId }) => (
  <Button
    variant="outline"
    size="sm"
    aria-label={`Copier ${what}`}
    data-testid={testId}
    onClick={async () => {
      try {
        await copyText(value);
        toast.success(copied);
      } catch {
        toast.error('Copie impossible : sélectionnez le texte pour le copier.');
      }
    }}
  >
    <Copy className="w-4 h-4 mr-2" aria-hidden="true" />
    Copier
  </Button>
);

// One line of « Payer par virement Interac »: what it is, its value, its copy button.
const PayLine = ({ label, children, copy }) => (
  <div className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between">
    <div className="min-w-0">
      <dt className="text-sm text-muted-foreground">{label}</dt>
      <dd className="font-medium break-all">{children}</dd>
    </div>
    {copy}
  </div>
);

const Detail = ({ label, children }) => (
  <div className="flex flex-col gap-1 py-2 sm:flex-row sm:justify-between sm:gap-4">
    <dt className="text-sm text-muted-foreground">{label}</dt>
    <dd className="text-sm sm:text-right">{children}</dd>
  </div>
);

const SubscriptionPanel = () => {
  const [subscription, setSubscription] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    subscriptionAPI.own()
      .then(({ data }) => setSubscription(data))
      .catch(() => setFailed(true));
  }, []);

  if (failed) {
    return <p className="text-muted-foreground" data-testid="subscription-unavailable">L'abonnement n'a pas pu être lu.</p>;
  }
  if (!subscription) return null;

  const { terms, payment } = subscription;
  const received = isReceived(payment);
  const price = shortMoney(terms.price_per_seat_cents, terms.currency);
  const total = money(subscription.monthly_total_cents, terms.currency);
  const period = PERIOD_WORDING[terms.period];
  const seats = `${subscription.seats} poste${subscription.seats > 1 ? 's' : ''}`;

  return (
    <Card data-testid="subscription" data-state={payment.state}>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="font-heading uppercase">Abonnement</CardTitle>
          <Badge variant={received ? 'default' : 'secondary'} data-testid="subscription-state">{stateLabel(payment)}</Badge>
        </div>
        <CardDescription>{terms.plan_name} · {subscription.organisation_name}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <Alert data-testid="subscription-state-alert">
          {received ? <CheckCircle2 className="h-4 w-4" aria-hidden="true" /> : <Clock className="h-4 w-4" aria-hidden="true" />}
          <AlertTitle>{stateLabel(payment)}</AlertTitle>
          <AlertDescription>
            {received
              ? 'Levitics a reçu votre paiement.'
              : 'Votre paiement est confirmé ici dès que Levitics l\'a reçu.'}
          </AlertDescription>
        </Alert>

        <div className="grid gap-6 sm:grid-cols-2">
          <div>
            <p className="text-sm text-muted-foreground">Montant {period}</p>
            <p className="text-3xl font-bold text-primary" data-testid="subscription-total">{total}</p>
            <p className="text-sm text-muted-foreground" data-testid="subscription-total-detail">
              {seats} × {price} · {terms.taxes ? TAXES_WORDING[terms.taxes] : <>taxes : <Pending term="taxes" /></>}
            </p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">À payer au plus tard le</p>
            {subscription.payment_due_on
              ? <p className="text-3xl font-bold text-primary" data-testid="subscription-due">{longDate(subscription.payment_due_on)}</p>
              : <p className="text-lg" data-testid="subscription-due"><Pending term="payment_due" /></p>}
          </div>
        </div>

        {!received && (
          <>
            <Separator />
            <section aria-labelledby="pay-by-interac">
              <h3 id="pay-by-interac" className="font-heading font-semibold uppercase">Payer par virement Interac</h3>
              <dl className="divide-y">
                <PayLine
                  label="Adresse du virement"
                  copy={terms.interac_email && (
                    <CopyButton value={terms.interac_email} what="l'adresse" copied="Adresse copiée" testId="copy-interac-email" />
                  )}
                >
                  {terms.interac_email
                    ? <span data-testid="interac-email">{terms.interac_email}</span>
                    : <Pending term="interac_email" />}
                </PayLine>
                <PayLine
                  label="Montant"
                  copy={<CopyButton value={plainAmount(subscription.monthly_total_cents)} what="le montant" copied="Montant copié" testId="copy-amount" />}
                >
                  <span data-testid="interac-amount">{total}</span>
                </PayLine>
                <PayLine
                  label="Référence à inscrire dans le message"
                  copy={<CopyButton value={subscription.reference} what="la référence" copied="Référence copiée" testId="copy-reference" />}
                >
                  <span className="font-mono text-lg tracking-wider" data-testid="subscription-reference">{subscription.reference}</span>
                </PayLine>
              </dl>
            </section>
          </>
        )}

        <Separator />
        <dl className="divide-y" aria-label="Détails de l'abonnement">
          <Detail label="Forfait">{terms.plan_name}</Detail>
          <Detail label="Postes">{subscription.seats}</Detail>
          <Detail label="Prix"><span data-testid="subscription-price">{priceWording(terms)}</span></Detail>
          <Detail label="Essai gratuit">
            <span data-testid="subscription-trial">
              {subscription.trial_ends_on ? 'du' : 'depuis le'}{' '}
              <span data-testid="subscription-trial-start">{longDate(subscription.trial_starts_on)}</span>
              {subscription.trial_ends_on
                ? <> au <span data-testid="subscription-trial-end">{longDate(subscription.trial_ends_on)}</span></>
                : <> · fin : <Pending term="trial_days" /></>}
            </span>
          </Detail>
          {received && <Detail label="Référence">{subscription.reference}</Detail>}
        </dl>
        <p className="text-xs text-muted-foreground">Le paiement par carte sera offert plus tard.</p>
      </CardContent>
    </Card>
  );
};

export default SubscriptionPanel;
